/**
 * Vue权限适配器 - 基础设施层
 * 将核心层权限服务适配到Vue框架
 */

import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { 
  V6Permission, 
  ContentPermissions,
  PermissionParser,
  PermissionMatcher,
  PermissionUtils 
} from '@/core/permission'
import { 
  UserProfileService, 
  type UserProfile,
  UserPermissionResolver 
} from '@/core/user'

/**
 * Vue权限适配器选项
 */
export interface VuePermissionOptions {
  autoCheckRoute?: boolean // 是否自动检查路由权限
  showPermissionWarnings?: boolean // 是否显示权限警告
  cacheDuration?: number // 缓存持续时间（毫秒）
  defaultUserRole?: string // 默认用户角色
  defaultIndustryCode?: string // 默认行业代码
}

/**
 * Vue权限适配器
 */
export class VuePermissionAdapter {
  private userProfileService: UserProfileService
  private currentUser = ref<UserProfile | null>(null)
  private userPermissions = ref<string[]>([])
  private options: VuePermissionOptions

  constructor(
    userProfileService: UserProfileService,
    options: VuePermissionOptions = {}
  ) {
    this.userProfileService = userProfileService
    this.options = {
      autoCheckRoute: true,
      showPermissionWarnings: true,
      cacheDuration: 5 * 60 * 1000, // 5分钟
      defaultUserRole: 'operator',
      defaultIndustryCode: 'AGR001',
      ...options
    }

    this.initialize()
  }

  /**
   * 初始化适配器
   */
  private async initialize(): Promise<void> {
    // 加载用户信息
    await this.loadUserInfo()

    // 设置自动检查路由权限
    if (this.options.autoCheckRoute) {
      this.setupRoutePermissionCheck()
    }

    // 监听用户信息变化
    this.setupUserInfoWatch()
  }

  /**
   * 加载用户信息
   */
  private async loadUserInfo(): Promise<void> {
    try {
      const userProfile = await this.userProfileService.getCurrentUser({
        includePermissions: true,
        forceRefresh: false
      })

      if (userProfile) {
        this.currentUser.value = userProfile
        this.userPermissions.value = userProfile.permissions
      } else {
        // 使用默认用户
        this.setDefaultUser()
      }
    } catch (error) {
      console.error('加载用户信息失败:', error)
      this.setDefaultUser()
    }
  }

  /**
   * 设置默认用户
   */
  private setDefaultUser(): void {
    this.currentUser.value = {
      id: 'default_user',
      tenantId: 'default_tenant',
      industryCode: this.options.defaultIndustryCode!,
      role: this.options.defaultUserRole!,
      username: '默认用户',
      permissions: [
        '*:operator:content:view',
        '*:operator:task:view',
        '*:operator:template:view'
      ]
    }
    this.userPermissions.value = this.currentUser.value.permissions
  }

  /**
   * 设置路由权限检查
   */
  private setupRoutePermissionCheck(): void {
    // 在实际项目中，这里应该集成到Vue Router中
    // 这里使用事件监听的方式
    window.addEventListener('route-change', (event: any) => {
      const route = event.detail.route
      if (route && route.meta && route.meta.permissions) {
        this.checkRoutePermission(route.meta.permissions, route.path)
      }
    })
  }

  /**
   * 设置用户信息监听
   */
  private setupUserInfoWatch(): void {
    watch(this.currentUser, (newUser) => {
      if (newUser) {
        // 用户信息变化时，更新权限列表
        this.userPermissions.value = newUser.permissions
        
        // 触发用户信息变化事件
        window.dispatchEvent(new CustomEvent('user-info-changed', {
          detail: { user: newUser }
        }))
      }
    })
  }

  /**
   * 检查用户是否有指定权限
   */
  hasPermission(permission: V6Permission | string): boolean {
    if (!this.currentUser.value) {
      return false
    }

    const normalizedPermission = typeof permission === 'string'
      ? V6Permission.fromString(permission)
      : permission

    // 平台管理员拥有所有权限
    if (this.currentUser.value.role === 'platform_admin') {
      return true
    }

    // 检查权限是否匹配
    return PermissionMatcher.canPerformAction(
      this.userPermissions.value,
      this.currentUser.value.role,
      this.currentUser.value.industryCode,
      normalizedPermission.module,
      normalizedPermission.action
    )
  }

  /**
   * 检查用户是否有任意权限
   */
  hasAnyPermission(permissions: (V6Permission | string)[]): boolean {
    return permissions.some(permission => this.hasPermission(permission))
  }

  /**
   * 检查用户是否有所有权限
   */
  hasAllPermissions(permissions: (V6Permission | string)[]): boolean {
    return permissions.every(permission => this.hasPermission(permission))
  }

  /**
   * 检查路由权限
   */
  checkRoutePermission(routePermissions: string[], routePath?: string): boolean {
    if (!routePermissions || routePermissions.length === 0) {
      return true // 无权限要求，默认允许访问
    }

    const hasPermission = this.hasAnyPermission(routePermissions)

    if (!hasPermission && this.options.showPermissionWarnings) {
      const message = routePath
        ? `无权限访问路径: ${routePath}`
        : '无权限访问此页面'
      
      ElMessage.warning(message)
    }

    return hasPermission
  }

  /**
   * 检查按钮权限（返回Vue计算属性）
   */
  useButtonPermission(permission: V6Permission | string) {
    const hasPermission = computed(() => this.hasPermission(permission))
    
    const checkButtonPermission = (): boolean => {
      if (!hasPermission.value && this.options.showPermissionWarnings) {
        ElMessage.warning('无权限执行此操作')
        return false
      }
      return true
    }

    return {
      hasPermission,
      checkButtonPermission
    }
  }

  /**
   * 检查行业访问权限
   */
  canAccessIndustry(industryCode: string): boolean {
    if (!this.currentUser.value) {
      return false
    }

    // 平台管理员可以访问所有行业
    if (this.currentUser.value.role === 'platform_admin') {
      return true
    }

    // 检查用户是否有该行业的权限
    return PermissionMatcher.canAccessIndustry(
      this.userPermissions.value,
      industryCode
    )
  }

  /**
   * 获取用户角色信息
   */
  getUserRoleInfo() {
    return computed(() => {
      if (!this.currentUser.value) {
        return {
          role: '',
          industryCode: '',
          isAdmin: false,
          isAnalyst: false,
          isOperator: false,
          isExpert: false,
          isBusinessOwner: false,
          isBusinessManager: false,
          isBusinessMember: false
        }
      }

      const role = this.currentUser.value.role
      
      return {
        role,
        industryCode: this.currentUser.value.industryCode,
        isAdmin: role === 'platform_admin',
        isAnalyst: role === 'platform_business',
        isOperator: role === 'platform_operator',
        isExpert: role === 'platform_expert',
        isBusinessOwner: role === 'business_owner',
        isBusinessManager: role === 'business_manager',
        isBusinessMember: role === 'business_member'
      }
    })
  }

  /**
   * 获取用户权限树
   */
  async getPermissionTree() {
    if (!this.currentUser.value) {
      return null
    }

    return UserPermissionResolver.getPermissionTree(
      this.userPermissions.value,
      this.currentUser.value.role,
      this.currentUser.value.industryCode,
      {
        expandWildcards: false,
        groupBy: 'industry'
      }
    )
  }

  /**
   * 获取用户可用行业列表
   */
  async getAvailableIndustries(): Promise<string[]> {
    if (!this.currentUser.value) {
      return []
    }

    return UserPermissionResolver.getAvailableIndustries(
      this.userPermissions.value,
      this.currentUser.value.role
    )
  }

  /**
   * 获取用户在某模块的可用操作
   */
  getAvailableActions(module: string): string[] {
    if (!this.currentUser.value) {
      return []
    }

    return UserPermissionResolver.getAvailableActions(
      this.userPermissions.value,
      this.currentUser.value.role,
      this.currentUser.value.industryCode,
      module
    )
  }

  /**
   * 模拟用户登录
   */
  async login(userInfo: Partial<UserProfile>): Promise<void> {
    try {
      // 在实际项目中，这里应该调用认证服务
      // 这里只是模拟登录
      const mockUser: UserProfile = {
        id: userInfo.id || 'user_' + Date.now(),
        tenantId: userInfo.tenantId || 'tenant_001',
        industryCode: userInfo.industryCode || this.options.defaultIndustryCode!,
        role: userInfo.role || this.options.defaultUserRole!,
        username: userInfo.username || '用户',
      permissions: userInfo.permissions || [
        '*:operator:content:view',
        '*:operator:task:view'
      ]
      }

      this.currentUser.value = mockUser
      this.userPermissions.value = mockUser.permissions

      // 保存到localStorage
      localStorage.setItem('v6_user_info', JSON.stringify(mockUser))

      ElMessage.success(`欢迎回来，${mockUser.username}`)
    } catch (error) {
      console.error('登录失败:', error)
      throw error
    }
  }

  /**
   * 模拟用户退出
   */
  logout(): void {
    this.currentUser.value = null
    this.userPermissions.value = []
    
    localStorage.removeItem('v6_user_info')
    ElMessage.info('已退出登录')
  }

  /**
   * 刷新用户信息
   */
  async refreshUserInfo(): Promise<void> {
    await this.loadUserInfo()
  }

  /**
   * 获取当前用户信息
   */
  getCurrentUser() {
    return computed(() => this.currentUser.value)
  }

  /**
   * 获取用户权限列表
   */
  getUserPermissions() {
    return computed(() => this.userPermissions.value)
  }
}

/**
 * 创建Vue权限适配器实例
 */
export function createVuePermissionAdapter(
  userProfileService: UserProfileService,
  options?: VuePermissionOptions
): VuePermissionAdapter {
  return new VuePermissionAdapter(userProfileService, options)
}